import type { ILoyaltyEvent, ILoyaltyProgram } from '@order-up/models';
import { createApiClient } from './apiClient';
import { createLogger } from '../utils/logger';
import { tenantLocationApiPath } from '@order-up/common';

const logger = createLogger('LoyaltyService');
const apiClient = createApiClient('/api/v1');

export interface LoyaltyBalance {
  customerUid: string;
  tenantDocId: string;
  restaurantDocId: string;
  balance: number;
  lifetimeEarned: number;
  lastEventAt?: string;
}

export interface ConfigureLoyaltyProgramBody {
  status: 'active' | 'paused' | 'archived';
  displayName: string;
  earnRule: { pointsPerCurrencyUnit: number; minimumSpendCents?: number };
  expiryPolicy: { daysUntilExpiry?: number };
  centsPerPoint: number;
}

export interface RecordLoyaltyEventBody {
  customerUid: string;
  type: 'earn' | 'burn' | 'adjust';
  points: number;
  idempotencyKey: string;
  orderId?: string;
  reason: string;
}

const getProgram = async (
  tenantDocId: string,
  locationDocId: string,
): Promise<ILoyaltyProgram | null> => {
  const response = (await apiClient.get(
    tenantLocationApiPath({ tenantDocId, locationDocId }, 'loyalty/program'),
  )) as any;
  return response.data?.program ?? null;
};

const configureProgram = async (
  tenantDocId: string,
  locationDocId: string,
  body: ConfigureLoyaltyProgramBody,
): Promise<ILoyaltyProgram> => {
  logger.debug('Configuring loyalty program', { tenantDocId, restaurantDocId: locationDocId });
  const response = (await apiClient.put(
    tenantLocationApiPath({ tenantDocId, locationDocId }, 'loyalty/program'),
    body,
  )) as any;
  return response.data.program;
};

const getBalance = async (
  tenantDocId: string,
  locationDocId: string,
  customerUid: string,
): Promise<LoyaltyBalance> => {
  const response = (await apiClient.get(
    tenantLocationApiPath({ tenantDocId, locationDocId }, `customers/${customerUid}/loyalty/balance`),
  )) as any;
  return response.data.balance;
};

const listEvents = async (
  tenantDocId: string,
  locationDocId: string,
  customerUid: string,
  limit?: number,
): Promise<ILoyaltyEvent[]> => {
  const response = (await apiClient.get(
    tenantLocationApiPath({ tenantDocId, locationDocId }, `customers/${customerUid}/loyalty/events`),
    { params: limit ? { limit: String(limit) } : undefined },
  )) as any;
  return response.data?.events ?? [];
};

const recordEvent = async (
  tenantDocId: string,
  locationDocId: string,
  body: RecordLoyaltyEventBody,
): Promise<ILoyaltyEvent> => {
  const response = (await apiClient.post(
    tenantLocationApiPath({ tenantDocId, locationDocId }, 'loyalty/events'),
    body,
  )) as any;
  return response.data.event;
};

// ─── Loyalty Analytics (F1 merchant loyalty analytics) ───────────────────────

export interface LoyaltyAnalytics {
  tenantGuid: string;
  rangeStart: string;
  rangeEnd: string;
  /** Distinct earners in the period. */
  memberCount: number;
  /** Total points earned in the period. */
  pointsEarned: number;
  /** Total points burned in the period (positive). */
  pointsBurned: number;
  /** All-time outstanding liability (snapshot, not period-scoped). */
  pointsLiability: number;
  /** pointsBurned / pointsEarned, 4 decimal places. 0 when nothing earned. */
  redemptionRate: number;
}

/**
 * GET /api/v1/reports/loyalty/analytics
 * Gated by `initiative.customer_loyalty` launch flag + requireManagementFeature('reports').
 * Analytics are tenant-scoped (loyalty_audit has no restaurant_guid column);
 * both IDs are passed for auth only.
 */
const getAnalytics = async (
  tenantId: number,
  locationNumericId: number,
  startDate: Date,
  endDate: Date,
): Promise<LoyaltyAnalytics> => {
  // The analytics endpoint lives under /api/v1/reports/loyalty/analytics.
  // The module-level apiClient already has base /api/v1; we extend the path.
  const response = (await apiClient.get('/reports/loyalty/analytics', {
    params: {
      tenantId,
      restaurantId: locationNumericId,
      startDate: startDate.toISOString(),
      endDate:   endDate.toISOString(),
    },
  })) as any;
  return response.data.analytics;
};

export const loyaltyService = {
  getProgram,
  configureProgram,
  getBalance,
  listEvents,
  recordEvent,
  getAnalytics,
};
